import CloseIcon from "@mui/icons-material/Close";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { NegotiationPaymentMethod } from "../../../../shared/services/api/sinister/SinisterNegotiationService";
import { IAgreementInstallment, SinisterAgreementService } from "../../../../shared/services/api/sinister/SinisterAgreementService";
import { formatDateFnsRemoveTimezone } from "../../../../shared/utils/format-date";
import { formatScaleValueBR } from "../../../../shared/utils/formatScaleValue";
import { PAYMENT_METHOD_OPTIONS } from "../../../creditRecovery/constants/paymentMethodOptions";

interface Props {
	open: boolean;
	agreementId: string;
	installment: IAgreementInstallment;
	onClose: () => void;
	onConfirm: () => void;
}

export const ModalManualSettleInstallment = ({ open, agreementId, installment, onClose, onConfirm }: Props) => {
	const toastHandler = useToast();
	const [paymentMethod, setPaymentMethod] = useState<NegotiationPaymentMethod>("boleto");
	const [paymentDate, setPaymentDate] = useState("");
	const [note, setNote] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		if (open) {
			setPaymentMethod("boleto");
			setPaymentDate(format(new Date(), "yyyy-MM-dd"));
			setNote("");
		}
	}, [open, installment.number]);

	const handleConfirm = async () => {
		setIsSubmitting(true);
		const result = await SinisterAgreementService.manualSettleInstallment(agreementId, installment.number, {
			paymentMethod,
			paymentDate: paymentDate || undefined,
			note: note || undefined,
		});
		setIsSubmitting(false);

		if (result instanceof Error) {
			toastHandler.present({ type: "error", messageOrContent: result.message, position: "top-right" });
			return;
		}

		toastHandler.present({
			type: "success",
			messageOrContent: `Baixa manual registrada para a parcela ${installment.number}.`,
			position: "top-right",
		});
		onConfirm();
	};

	return (
		<>
			<LoadingComp isopen={isSubmitting} />
			<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typography variant="h6" color="primary" sx={{ fontWeight: 400 }}>
						Baixa Manual
					</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					<Stack spacing={2} mt={0.5}>
						<Alert severity="warning" sx={{ py: 0.5 }}>
							Parcela {installment.number} — {formatScaleValueBR(String(installment.value))} — vencimento em{" "}
							{formatDateFnsRemoveTimezone(installment.dueDate) || "-"}. Isso irá marcar a parcela como paga manualmente, fora do fluxo de
							pagamento padrão.
						</Alert>

						<TextField
							select
							fullWidth
							size="small"
							label="Forma de pagamento"
							value={paymentMethod}
							onChange={(e) => setPaymentMethod(e.target.value as NegotiationPaymentMethod)}
						>
							{PAYMENT_METHOD_OPTIONS.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</TextField>

						<TextField
							type="date"
							fullWidth
							size="small"
							label="Data do pagamento"
							value={paymentDate}
							onChange={(e) => setPaymentDate(e.target.value)}
							InputLabelProps={{ shrink: true }}
						/>

						<TextField
							fullWidth
							size="small"
							multiline
							minRows={2}
							label="Observação (opcional)"
							value={note}
							onChange={(e) => setNote(e.target.value)}
							placeholder="Ex: cliente pagou diretamente na imobiliária."
						/>
					</Stack>
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose}>Voltar</Button>
					<Button variant="contained" disabled={isSubmitting} onClick={handleConfirm}>
						Confirmar Baixa
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
